import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Search, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import { SectionTag } from "@/components/site/Reveal";
import ParticleField from "@/components/site/ParticleField";
import { fetchCases, toAbsolute } from "@/lib/api";

export default function CaseStudies() {
  const { t, dir, lang } = useLang();
  const [cases, setCases] = useState(null);
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("");
  const Arrow = dir === "rtl" ? ArrowLeft : ArrowRight;

  useEffect(() => {
    fetchCases(lang).then((r) => setCases(r.data)).catch(() => setCases([]));
  }, [lang]);

  const sectors = useMemo(() => Array.from(new Set((cases || []).map((c) => c.sector).filter(Boolean))), [cases]);

  const filtered = useMemo(() => {
    if (!cases) return null;
    const s = q.trim().toLowerCase();
    return cases.filter((c) => {
      if (sector && c.sector !== sector) return false;
      if (!s) return true;
      const hay = `${c.title} ${c.summary || ""} ${c.sector || ""} ${c.client || ""} ${(c.tags || []).join(" ")}`.toLowerCase();
      return hay.includes(s);
    });
  }, [cases, q, sector]);

  return (
    <SiteLayout>
      <section className="relative overflow-hidden pt-16 pb-10" data-testid="cases-page">
        <ParticleField className="opacity-40" density={30} />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
          <SectionTag>{t.cases.tag}</SectionTag>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-5 mb-4">{t.cases.title}</h1>
          <p className="text-slate-400 text-lg max-w-2xl">{t.cases.subtitle}</p>
        </div>
      </section>

      <section className="section-pad pt-6">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-8 flex flex-col md:flex-row gap-3" data-testid="cases-filters">
            <div className="relative flex-1">
              <Search size={16} className="absolute top-1/2 -translate-y-1/2 start-4 text-slate-500 pointer-events-none" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.filters.searchCases}
                className="w-full rounded-xl bg-[#0d1836] border border-white/10 ps-11 pe-10 py-3 text-sm focus:outline-none focus:border-[color:var(--primary)]"
                data-testid="cases-search-input"
              />
              {q && (
                <button onClick={() => setQ("")} className="absolute top-1/2 -translate-y-1/2 end-3 h-6 w-6 rounded-full grid place-items-center hover:bg-white/10 text-slate-400" aria-label="clear">
                  <X size={14} />
                </button>
              )}
            </div>
            {sectors.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSector("")}
                  className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${!sector ? "text-[#04120a]" : "border-white/15 hover:border-[color:var(--primary)] text-slate-300"}`}
                  style={!sector ? { background: "var(--primary)", borderColor: "var(--primary)" } : {}}
                  data-testid="cases-filter-all"
                >
                  {t.filters.all}
                </button>
                {sectors.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSector(sector === s ? "" : s)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${sector === s ? "text-[#04120a]" : "border-white/15 hover:border-[color:var(--primary)] text-slate-300"}`}
                    style={sector === s ? { background: "var(--primary)", borderColor: "var(--primary)" } : {}}
                    data-testid={`cases-filter-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          {filtered === null ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-20" data-testid="cases-empty">{q || sector ? t.filters.noResults : t.cases.empty}</p>
          ) : (
            <div className="space-y-6">
              {filtered.map((c, i) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6 }}
                >
                  <Link to={`/case-studies/${encodeURIComponent(c.slug)}`} className="group grid md:grid-cols-2 gap-0 card-surface rounded-3xl overflow-hidden" data-testid={`case-card-${i}`}>
                    <div className={`relative aspect-video md:aspect-auto min-h-[240px] overflow-hidden ${i % 2 ? "md:order-2" : ""}`}>
                      <img src={toAbsolute(c.cover_image)} alt={c.title} loading="lazy" className="h-full w-full object-cover scale-105 group-hover:scale-110 transition-transform duration-700" />
                    </div>
                    <div className="p-8 flex flex-col justify-center" dir="auto">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {c.sector && <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300">{c.sector}</span>}
                        {(c.tags || []).slice(0, 2).map((tag) => (
                          <span key={tag} className="rounded-full px-3 py-1 text-xs" style={{ background: "rgba(0,242,96,0.12)", color: "var(--primary)" }}>{tag}</span>
                        ))}
                      </div>
                      <h3 className="font-extrabold text-2xl mb-3 leading-snug group-hover:text-primary transition-colors">{c.title}</h3>
                      <p className="text-slate-400 leading-relaxed mb-5">{c.summary}</p>
                      <span className="inline-flex items-center gap-2 text-sm font-bold text-primary">
                        {t.cases.viewCase} <Arrow size={16} className="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
